import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith, tap, withLatestFrom } from 'rxjs/operators';
import { LogCategory, LogType, LogWidget, LoggerService } from 'src/app/core/services/logger.service';
import { TransferCreditsService } from 'src/app/services/transfer-credits.service';
import { ProfileService } from '../../../core/services/profile.service';
import { UtilityService } from '../../../core/services/utility.service';

@Component({
    selector: 'app-approval-new-request-list',
    templateUrl: './approval-new-request-list.component.html',
    styleUrls: ['./approval-new-request-list.component.scss'],
    standalone: false
})
export class ApprovalNewRequestListComponent implements OnInit, OnDestroy {

    @Output() editRequest = new EventEmitter<any>();

    private readonly LOCAL_FILTER_NAME: string = 'myke.transfer-credits-filters';
    private localFilters: any;
    //  Tracks the changes in the form controls for the filter inputs
    public selectedCountry = new FormControl<string>('');
    public selectedInstitution = new FormControl<string>('');
    public selectedDept = new FormControl<string>('');
    public selectedCoreCourses = new FormControl<string[]>([]);
    public selectedGenEds = new FormControl<string[]>([]);
    public selectedAoEs = new FormControl<string[]>([]);
    public selectedCourseName = new FormControl<string>('');

    //  Approvals with every filter applied EXCEPT the course-name typeahead.
    //  Used to source typeahead suggestions so they narrow with the other active filters
    //  without eating themselves as the user types.
    private approvalsExceptCourseName$: Observable<any[]>;
    public filteredApprovals$: Observable<any[]>;
    public pagedApprovals$: Observable<any[]>;
    public currentPage$ = new BehaviorSubject<number>(1); // BehaviorSubject to track the current page
    public totalPages$: Observable<number>;
    public pageSize: number = 10;
    constructor(
        private logger: LoggerService,
        public transferCredits: TransferCreditsService,
        public profile: ProfileService,
        public utilityService: UtilityService,
    ) {
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Widget, LogWidget.TransferCredits, category);
    }
       
    public ngOnDestroy(): void {          
        this.log("ngOnDestroy", "", LogCategory.Destroy);
    }

    public ngOnInit(): void {
        this.localFilters = UtilityService.loadLocalData(this.LOCAL_FILTER_NAME);
        //  Helps us make sure we aren't resetting anything when the component inits
        let isInit = true; 

        if (!this.localFilters)
            this.localFilters = {};

        this.log("ngOnInit", null, LogCategory.Init);

        //  If stored filters are found apply them.
        if (!!this.localFilters?.filters)
            Object.keys(this.localFilters.filters).forEach(f => this.updateFromStorage(f));

        this.approvalsExceptCourseName$ = combineLatest([
            this.transferCredits.preApprovals$,
            this.selectedCountry.valueChanges.pipe(startWith(this.selectedCountry.value)),
            this.selectedInstitution.valueChanges.pipe(startWith(this.selectedInstitution.value)),
            this.selectedDept.valueChanges.pipe(startWith(this.selectedDept.value)),
            this.selectedCoreCourses.valueChanges.pipe(startWith(this.selectedCoreCourses.value)),
            this.selectedGenEds.valueChanges.pipe(startWith(this.selectedGenEds.value)),
            this.selectedAoEs.valueChanges.pipe(startWith(this.selectedAoEs.value)),
        ]).pipe(
            map(([approvals, country, institution, dept, coreCourses, genEds, areasOfEmphasis]) => {
                //  This is returning as an object occasionally and throwing errors.
                if (!Array.isArray(approvals))
                    return ([]);

                return approvals.filter((course: {
                        HostCountry: string;
                        HostProgram: string;
                        UncDepartment: string;
                        UncPrimaryCourseNumber: string;
                        GeneralEducation: string[];
                        AreasOfEmphasis: string[];
                    }) =>
                    (!country || course.HostCountry === country) &&
                    (!institution || course.HostProgram === institution) &&
                    (!dept || course.UncDepartment === dept) &&
                    (!coreCourses || coreCourses.length === 0 || coreCourses.includes(course.UncPrimaryCourseNumber)) &&
                    (!genEds || genEds.length === 0 || (
                        Array.isArray(course.GeneralEducation) &&
                        course.GeneralEducation.some(ge => genEds.includes(ge))
                    )) &&
                    (!areasOfEmphasis || areasOfEmphasis.length === 0 || (
                        Array.isArray(course.AreasOfEmphasis) &&
                        course.AreasOfEmphasis.some(ae => areasOfEmphasis.includes(ae))
                    ))
                );
            }),
            shareReplay(1),
        );

        this.filteredApprovals$ = combineLatest([
            this.approvalsExceptCourseName$,
            this.selectedCourseName.valueChanges.pipe(startWith(this.selectedCourseName.value)),
        ]).pipe(
            //  Persist all filter selections. Runs once per change: approvalsExceptCourseName$
            tap(() => {
                if (isInit) return;
                this.localFilters.filters = {
                    selectedCountry: this.selectedCountry.value,
                    selectedInstitution: this.selectedInstitution.value,
                    selectedDept: this.selectedDept.value,
                    selectedCoreCourses: this.selectedCoreCourses.value,
                    selectedGenEds: this.selectedGenEds.value,
                    selectedAoEs: this.selectedAoEs.value,
                    selectedCourseName: this.selectedCourseName.value,
                };
                UtilityService.saveLocalData(this.LOCAL_FILTER_NAME, this.localFilters);
            }),
            map(([approvals, courseName]) => {
                const nameQuery = (courseName || '').trim().toLowerCase();
                if (!nameQuery) return approvals;
                return approvals.filter((course: { HostCourseName: string }) =>
                    (course.HostCourseName || '').toLowerCase().includes(nameQuery)
                );
            }),
            //  Reset to page 1 on filter change, or restore the saved page on initial load.
            tap(() => {
                if (isInit && !!this.localFilters?.currentPage)
                    this.currentPage$.next(this.localFilters.currentPage);
                else
                    this.currentPage$.next(1);
                isInit = false;
            }),
            shareReplay(1),
        );

        this.totalPages$ = this.filteredApprovals$.pipe(
            map(list => list && this.pageSize ? Math.ceil(list.length / this.pageSize) : 1)
        ); 

        // Add paging logic
        this.pagedApprovals$ = combineLatest([
            this.filteredApprovals$,
            this.currentPage$
        ]).pipe(
            tap(([, currentPage]) => {
                this.localFilters.currentPage = currentPage;
                UtilityService.saveLocalData(this.LOCAL_FILTER_NAME, this.localFilters);
            }),
            map(([approvals, currentPage]) => {
                const startIndex = (currentPage - 1) * this.pageSize;
                return approvals.slice(startIndex, startIndex + this.pageSize);
            })
        );
    }

    public clearFilters() {
        this.selectedCountry.setValue('');
        this.selectedInstitution.setValue('');
        this.selectedDept.setValue('');
        this.selectedCoreCourses.setValue([]);
        this.selectedGenEds.setValue([]);
        this.selectedAoEs.setValue([]);
        this.selectedCourseName.setValue('');
        this.currentPage$.next(1);
        UtilityService.deleteLocalData(this.LOCAL_FILTER_NAME);
    }

    public clearCourseName(): void {
        this.selectedCourseName.setValue('');
    }

    public searchCourseName = (text$: Observable<string>): Observable<string[]> =>
        text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            withLatestFrom(this.approvalsExceptCourseName$),
            map(([term, approvals]) => {
                const query = (term || '').trim().toLowerCase();
                if (!query || !Array.isArray(approvals)) return [];
                const seen = new Set<string>();
                const results: string[] = [];
                for (const c of approvals) {
                    const name: string = c?.HostCourseName;
                    if (!name || seen.has(name)) continue;
                    if (name.toLowerCase().includes(query)) {
                        seen.add(name);
                        results.push(name);
                        if (results.length >= 10) break;
                    }
                }
                return results;
            })
        );

    public addArrayValue(event: Event, control: FormControl) {
        const target = (event.target as HTMLSelectElement);
        const current = control.value;
        const updated = current.includes(target.value)
            ? current.filter(c => c !== target.value)
            : [...current, target.value];

        control.setValue(updated);

        // Reset dropdown to show the default value
        target.selectedIndex = 0;
    }

    public removeArrayValue<T>(control: FormControl<T[]>, value: T): void {
        const current = control.value;
        const updated = current.filter(c => c !== value);

        control.setValue(updated);
    }

    public nextPage(): void {
        this.currentPage$.next(this.currentPage$.value + 1);
    }

    public previousPage(): void {
        if (this.currentPage$.value > 1) {
            this.currentPage$.next(this.currentPage$.value - 1);
        }
    }

    public onEditRequest(courseId: number): void {
        this.transferCredits.courseId$.next(courseId);
        this.editRequest.emit(courseId);
    }

    private updateFromStorage(filterName: string) {
        if (this.localFilters.filters.hasOwnProperty(filterName))
            this[filterName].setValue(this.localFilters.filters[filterName]);
    }
} 

